<template>
  <div class="workbench">
    <!-- 欢迎语 -->
    <div class="greeting">
      <h2>{{ greeting }}，{{ username }}</h2>
      <p class="date">{{ today }}</p>
    </div>

    <!-- 收藏功能 -->
    <el-card class="section-card">
      <div slot="header">
        <span>⭐ 收藏功能</span>
      </div>
      <div v-if="favorites.length" class="card-grid">
        <div
          v-for="item in favorites"
          :key="item.id"
          class="func-card starred"
          @click="goTo(item)"
        >
          <i :class="item.icon"></i>
          <span>{{ item.name }}</span>
          <i class="el-icon-star-on star-icon" @click.stop="toggleFavorite(item)"></i>
        </div>
      </div>
      <div v-else class="empty-hint">暂无收藏，点击全部功能中的 ⭐ 收藏常用功能</div>
    </el-card>

    <!-- 最近使用 -->
    <el-card class="section-card" v-if="recents.length">
      <div slot="header">
        <span>🕐 最近使用</span>
      </div>
      <div class="card-grid">
        <div
          v-for="item in recents"
          :key="item.path"
          class="func-card"
          @click="goTo(item)"
        >
          <i :class="item.icon"></i>
          <span>{{ item.name }}</span>
        </div>
      </div>
    </el-card>

    <!-- 全部功能 -->
    <el-card class="section-card">
      <div slot="header">
        <span>📋 全部功能</span>
        <el-input
          v-model="searchText"
          placeholder="搜索功能名称"
          size="small"
          style="width: 200px; float: right; margin-top: -3px;"
          clearable
        />
      </div>
      <div v-for="(group, idx) in filteredMenuGroups" :key="group.name + '_' + idx" class="menu-group">
        <div class="group-title">{{ group.name }}</div>
        <div class="card-grid">
          <div
            v-for="child in group.children"
            :key="child.id"
            class="func-card"
            @click="goTo(child)"
          >
            <i :class="child.icon"></i>
            <span>{{ child.name }}</span>
            <i
              :class="isFavorited(child.id) ? 'el-icon-star-on star-icon' : 'el-icon-star-off star-icon-off'"
              @click.stop="toggleFavorite(child)"
            ></i>
          </div>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getFavorites, addFavorite, removeFavorite } from '@/api/favorite'

const RECENT_KEY = 'wms_recent_pages'

export default {
  name: 'Workbench',
  data() {
    return {
      username: localStorage.getItem('username') || '用户',
      favorites: [],
      searchText: ''
    }
  },
  computed: {
    greeting() {
      const h = new Date().getHours()
      if (h < 12) return '早上好'
      if (h < 18) return '下午好'
      return '晚上好'
    },
    today() {
      return new Date().toLocaleDateString('zh-CN', {
        year: 'numeric', month: 'long', day: 'numeric', weekday: 'long'
      })
    },
    menuTree() {
      const tree = localStorage.getItem('menuTree')
      return tree ? JSON.parse(tree) : []
    },
    recents() {
      try {
        return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
      } catch { return [] }
    },
    filteredMenuGroups() {
      const groups = []
      const keyword = this.searchText.toLowerCase()
      this.menuTree.forEach(menu => {
        if (menu.children && menu.children.length) {
          const filtered = menu.children.filter(c =>
            !keyword || (c.name && c.name.toLowerCase().includes(keyword))
          )
          if (filtered.length) {
            groups.push({ name: menu.name, children: filtered })
          }
        } else if (!keyword || (menu.name && menu.name.toLowerCase().includes(keyword))) {
          if (!groups.length || groups[groups.length - 1].name !== '其他') {
            groups.push({ name: '其他', children: [] })
          }
          groups[groups.length - 1].children.push(menu)
        }
      })
      return groups
    }
  },
  async created() {
    await this.loadFavorites()
    this.username = localStorage.getItem('username') || '用户'
  },
  methods: {
    async loadFavorites() {
      try {
        const list = await getFavorites()
        this.favorites = list || []
      } catch { this.favorites = [] }
    },
    isFavorited(permissionId) {
      return this.favorites.some(f => f.permissionId === permissionId || f.id === permissionId)
    },
    async toggleFavorite(item) {
      const pid = item.permissionId || item.id
      if (this.isFavorited(pid)) {
        try { await removeFavorite(pid) } catch { /* ignore */ }
        this.favorites = this.favorites.filter(f => (f.permissionId || f.id) !== pid)
      } else {
        try { await addFavorite(pid) } catch { /* ignore */ }
        this.favorites.push({ permissionId: pid, name: item.name, path: item.path, icon: item.icon })
      }
    },
    goTo(item) {
      if (!item.path) return
      // 记录最近使用
      const recents = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
      const idx = recents.findIndex(r => r.path === item.path)
      if (idx >= 0) recents.splice(idx, 1)
      recents.unshift({ permissionId: item.id || item.permissionId, name: item.name, path: item.path, icon: item.icon })
      if (recents.length > 6) recents.length = 6
      localStorage.setItem(RECENT_KEY, JSON.stringify(recents))
      this.$router.push(item.path)
    }
  }
}
</script>

<style scoped>
.workbench { padding: 24px; background: #f0f2f5; min-height: 100vh; }
.greeting { text-align: center; padding: 40px 0 20px; }
.greeting h2 { margin: 0; font-size: 26px; color: #303133; }
.date { color: #909399; margin-top: 8px; }

.section-card { margin-bottom: 20px; border-radius: 10px; }

.card-grid { display: flex; flex-wrap: wrap; gap: 12px; }
.func-card {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 20px; background: #fff; border: 1px solid #e4e7ed;
  border-radius: 8px; cursor: pointer; transition: all 0.2s;
  min-width: 160px; flex-shrink: 0;
}
.func-card:hover { box-shadow: 0 2px 12px rgba(0,0,0,0.1); border-color: #409EFF; }
.func-card i:first-child { font-size: 18px; color: #409EFF; }
.func-card.starred { background: #fdf6ec; border-color: #e6a23c; }
.star-icon { font-size: 16px; color: #e6a23c; margin-left: auto; cursor: pointer; }
.star-icon-off { font-size: 16px; color: #c0c4cc; margin-left: auto; cursor: pointer; }
.star-icon-off:hover { color: #e6a23c; }

.menu-group { margin-bottom: 16px; }
.group-title { font-size: 14px; font-weight: 600; color: #606266; margin-bottom: 8px; padding-left: 4px; border-left: 3px solid #409EFF; }
.empty-hint { text-align: center; color: #909399; padding: 30px 0; }
</style>
